import React, { useState, useMemo, useEffect, useCallback } from 'react';
import classNames from 'classnames';
// eslint-disable-next-line import/no-unresolved
import {
  Index,
  Configure,
  useInstantSearch,
  useSearchBox,
  SearchBox,
} from 'react-instantsearch';

import {
  ALGOLIA_INDEX_NAME,
  SORT_BY_OPTIONS,
  ALGOLIA_PRODUCT_INDEXES,
} from 'commons/constants';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import ProductListingPageName from 'components/consumer/ProductListingPage/components/ProductListingPageName';
import DesktopShowHideFiltersButton from 'components/consumer/ProductListingPage/components/desktop/DesktopShowHideFiltersButton';
import MobileGridColumnToggle from 'components/consumer/ProductListingPage/components/mobile/MobileGridColumnToggle';
import DesktopProductListingSidebarContainer from 'components/consumer/ProductListingPage/components/desktop/DesktopProductListingSidebarContainer';
import useScreen from 'hooks/useScreen';
import { transformInspirationImageUrl } from 'components/consumer/inspiration-photos-grid/search-state/utils';
import CustomerNameWithEmoji from 'components/consumer/CustomerNameWithEmoji';
import ProductListingPageDescriptionUI from '../ProductListingPageDescriptionUI';
import AdvancedSearchSidebar from '../AdvancedSearchSidebar';
import AdvancedSearchProductHits from '../AdvancedSearchProductHits';
import useAdvancedSearchContext from '../../hooks/useAdvancedSearchContext';

import CustomerPhotos from '../../pages/CustomerPhotos';
import CustomerReviews from '../../pages/CustomerReviews';
import StaticPages from '../../pages/StaticPages';

import DesktopProductListingColorFilter from './components/DesktopProductListingColorFilter';
import ProductListingSortBy from './components/ProductListingSortBy';
import MobileProductFilters from './components/MobileProductFilters';

// Helper component to sync query from parent to nested Index
const VirtualSearchBox = ({ parentQuery }) => {
  const { query, refine } = useSearchBox();

  useEffect(() => {
    if (parentQuery && parentQuery !== query) {
      refine(parentQuery);
    }
  }, [parentQuery, query, refine]);

  return null;
};

const AlgoliaHits = ({
  loading = false,
  pageName = '',
  variant = 'advanced-search',
  isFinalProductCountKnown = true,
  isDesktopSidebarOpen = true,
  pageLongDescription = '',
}) => {
  const { isMobile } = useScreen();
  const {
    currentSearchSection,
    currentProductIndex,
    isReadyToShipActive,
    minWidth,
    maxWidth,
  } = useAdvancedSearchContext();
  const { results, status } = useInstantSearch();
  const { query: parentQuery } = useSearchBox();

  const [delayedResultsCount, setDelayedResultsCount] = useState(null);
  const resultsCountDelayMs = 750;
  const isSearchPending = status === 'loading' || status === 'stalled';

  const getCustomProps = useCallback(selectedGridItem => {
    if (!selectedGridItem) {
      return {};
    }

    const customerName = `${selectedGridItem.customer_first_name || ''} ${
      selectedGridItem.customer_last_name &&
      selectedGridItem.customer_last_name[0]
        ? `${selectedGridItem.customer_last_name[0]}.`
        : ''
    }`.trim();

    const products = selectedGridItem.products || [];

    return {
      imageProps: {
        src:
          transformInspirationImageUrl(
            selectedGridItem.image_link ||
              selectedGridItem.photo_image ||
              selectedGridItem.photo_url
          ) || '',
        trim: 'auto',
      },
      selectedItemName: (
        <CustomerNameWithEmoji>{customerName}</CustomerNameWithEmoji>
      ),
      selectedItemNameMobile: (
        <span className="CustomerPhotosCustomerNameMobile">{customerName}</span>
      ),
      selectedItemProducts: products,
      selectedItemMaterialList: products.map(p => p.upholstery).filter(Boolean),
      selectedItemDescription:
        selectedGridItem.photo_comment ||
        selectedGridItem.photo_description ||
        '',
      showDescriptionQuoteMark: true,
    };
  }, []);

  const [isSidebarOpen, setIsDesktopSidebarOpen] = useState(
    isDesktopSidebarOpen
  );
  const toggleSidebar = () => {
    setIsDesktopSidebarOpen(prevIsOpen => !prevIsOpen);
  };

  // Prefer SearchBox query (source of truth) – results.query can be stale/empty when filters change (e.g. ready to ship)
  const searchTerm = parentQuery || results?.query || pageName || '';
  const displayText = searchTerm ? `"${searchTerm}"` : '""';

  const productsCount = results?.nbHits || 0;

  useEffect(() => {
    if (isSearchPending || !isFinalProductCountKnown) {
      setDelayedResultsCount(null);
      return undefined;
    }
    if (productsCount > 0) {
      const timer = setTimeout(() => {
        setDelayedResultsCount(productsCount);
      }, resultsCountDelayMs);
      return () => clearTimeout(timer);
    }
    setDelayedResultsCount(null);
    return undefined;
  }, [
    productsCount,
    isSearchPending,
    isFinalProductCountKnown,
    resultsCountDelayMs,
  ]);

  const resultsCountText =
    !loading &&
    isFinalProductCountKnown &&
    delayedResultsCount != null &&
    delayedResultsCount > 0
      ? `${delayedResultsCount} Results`
      : null;

  const secondaryFacetFilters = useMemo(
    () => (isReadyToShipActive ? ['is_in_stock:true'] : []),
    [isReadyToShipActive]
  );

  // Align with Alexandria getProductsStaticData(showSearchProductsOnly): visibility 2 or 3, item_availability 0
  const secondaryNumericFilters = useMemo(() => {
    const filters = ['item_availability=0', 'visibility>1', 'visibility<4'];

    const minNum = parseInt(minWidth, 10);
    const maxNum = parseInt(maxWidth, 10);

    if (!Number.isNaN(minNum) && minNum >= 1) {
      filters.push(`width>=${minNum}`);
    }

    if (!Number.isNaN(maxNum) && maxNum <= 300) {
      filters.push(`width<=${maxNum}`);
    }

    return filters;
  }, [minWidth, maxWidth]);

  return (
    <FixedWidthContainer
      wide
      noMobilePadding
      noPadding={variant === 'advanced-search'}
    >
      <div
        className={classNames(
          'px-3 py-4 space-y-1',
          'md:px-[3.333vw] md:py-0 md:space-y-0 min-[1500px]:px-[50px]',
          'md:items-center md:bg-white md:flex md:h-[53px] md:sticky md:z-[11] md:gap-5 md:justify-between',
          'md:mt-4',
          'pt-0 md:pt-0',
          {
            'md:top-[55px]': variant === 'advanced-search',
            'md:top-[117px]': !variant || variant !== 'advanced-search',
          }
        )}
      >
        <div className="md:flex md:gap-5 md:items-center min-w-0">
          <div className="md:flex md:gap-2 md:items-center md:flex-nowrap min-w-0">
            <ProductListingPageName
              pageName={
                <>
                  Results for {displayText}
                  {resultsCountText && (
                    <span className="text-sm text-gray-light2 font-normal ml-3">
                      {resultsCountText}
                    </span>
                  )}
                </>
              }
              showPageNameWhileLoading={variant === 'advanced-search'}
            />
          </div>

          <DesktopShowHideFiltersButton
            loading={loading}
            onClick={toggleSidebar}
          />
        </div>

        <div
          className={classNames(
            'flex flex-row justify-between gap-3 py-3',
            'md:block md:gap-0 md:py-0'
          )}
        >
          <div
            className={classNames(
              'flex-1 flex gap-1',
              'md:flex-initial md:block md:gap-0'
            )}
          >
            {ALGOLIA_PRODUCT_INDEXES.includes(currentSearchSection) &&
              isMobile && (
                <>
                  <MobileProductFilters productsCount={productsCount} />
                  <div className="flex-1 min-w-[140px] flex items-center">
                    <ProductListingSortBy
                      sortByOptions={SORT_BY_OPTIONS.filter(
                        option => option.value !== 'newest'
                      )}
                    />
                  </div>
                </>
              )}

            {ALGOLIA_PRODUCT_INDEXES.includes(currentSearchSection) &&
              !isMobile && (
                <div className="flex-1 md:flex-initial md:flex md:flex-row md:gap-4 md:items-center">
                  <DesktopProductListingColorFilter />
                  <ProductListingSortBy
                    sortByOptions={SORT_BY_OPTIONS.filter(
                      option => option.value !== 'newest'
                    )}
                  />
                </div>
              )}
          </div>

          <MobileGridColumnToggle />
        </div>
      </div>

      <div className="md:flex md:flex-row md:gap md:mt-2 md:px-[3.333vw] min-[1500px]:px-[50px]">
        {!isMobile && (
          <DesktopProductListingSidebarContainer isOpen={isSidebarOpen}>
            <AdvancedSearchSidebar />
          </DesktopProductListingSidebarContainer>
        )}

        <div className="md:space-y-8 md:mb-8 md:flex-1">
          {currentSearchSection === ALGOLIA_INDEX_NAME.PRODUCTS &&
            currentProductIndex === ALGOLIA_INDEX_NAME.PRODUCTS && (
              <AdvancedSearchProductHits />
            )}
          {currentSearchSection === ALGOLIA_INDEX_NAME.PRODUCTS &&
            currentProductIndex !== ALGOLIA_INDEX_NAME.PRODUCTS && (
              <Index indexName={currentProductIndex}>
                <SearchBox classNames={{ root: 'hidden' }} />
                <VirtualSearchBox parentQuery={searchTerm} />
                <Configure
                  facets={['*']}
                  facetFilters={secondaryFacetFilters}
                  numericFilters={secondaryNumericFilters}
                  attributesToRetrieve={[
                    '_highlightResult',
                    'product_id',
                    'name',
                    'is_in_stock',
                  ]}
                  clickAnalytics
                />
                <AdvancedSearchProductHits />
              </Index>
            )}

          {currentSearchSection === ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS && (
            <Index indexName={ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS}>
              <SearchBox classNames={{ root: 'hidden' }} />
              <VirtualSearchBox parentQuery={searchTerm} />
              <Configure
                facets={['*']}
                numericFilters=""
                facetFilters=""
                attributesToRetrieve={['*']}
              />
              <CustomerPhotos getCustomProps={getCustomProps} />
            </Index>
          )}

          {currentSearchSection === ALGOLIA_INDEX_NAME.REVIEWS && (
            <Index indexName={ALGOLIA_INDEX_NAME.REVIEWS}>
              <SearchBox classNames={{ root: 'hidden' }} />
              <VirtualSearchBox parentQuery={searchTerm} />
              <Configure
                facets={['*']}
                numericFilters=""
                facetFilters=""
                attributesToRetrieve={[
                  'review_id',
                  'review_full_name',
                  'review_rate',
                  'review_comment',
                  'review_title',
                  'review_date',
                  'product_name',
                  'product_url',
                  'sef_url',
                  'image_link',
                  'fk_product_id',
                ]}
              />
              <CustomerReviews />
            </Index>
          )}

          {currentSearchSection === ALGOLIA_INDEX_NAME.STATIC_PAGES && (
            <Index indexName={ALGOLIA_INDEX_NAME.STATIC_PAGES}>
              <SearchBox classNames={{ root: 'hidden' }} />
              <VirtualSearchBox parentQuery={searchTerm} />
              <Configure
                numericFilters={[
                  'is_active=1',
                  'show_in_search=1',
                  'fk_store_id=1',
                ]}
                attributesToRetrieve={['*']}
              />
              <StaticPages />
            </Index>
          )}

          <ProductListingPageDescriptionUI
            pageLongDescription={pageLongDescription}
            pageName={pageName}
          />
        </div>
      </div>
    </FixedWidthContainer>
  );
};

export default AlgoliaHits;
